/**
 * SEARCH_ITEMS — static navigation items for the CommandPalette.
 *
 * Each item has:
 *  - id: unique string
 *  - labelKey: i18n key for the display name
 *  - descriptionKey?: i18n key for a subtitle/description
 *  - href: react-router navigate target
 *  - keywords: extra match terms (Romanian + English synonyms, not displayed)
 *  - group: 'nav' | 'settings' | 'actions'
 *
 * These are NAVIGATION-only — no live API queries.
 */

export interface SearchItem {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  href: string;
  keywords: string[];
  group: 'nav' | 'settings' | 'actions';
}

export const SEARCH_ITEMS: SearchItem[] = [
  // ── Navigation ──────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    descriptionKey: 'search.desc.dashboard',
    href: '/',
    keywords: ['acasa', 'home', 'panou', 'overview', 'start', 'principal'],
    group: 'nav',
  },
  {
    id: 'collections',
    labelKey: 'nav.collections',
    descriptionKey: 'search.desc.collections',
    href: '/operations/collections',
    keywords: ['colectari', 'intrari', 'receptie', 'nir', 'intake', 'entries', 'colectare'],
    group: 'nav',
  },
  {
    id: 'collections-new',
    labelKey: 'search.label.newCollection',
    descriptionKey: 'search.desc.newCollection',
    href: '/operations/collections/new',
    keywords: ['colectare noua', 'adauga colectare', 'receptie noua', 'new collection', 'add collection'],
    group: 'actions',
  },
  {
    id: 'documents',
    labelKey: 'nav.documents',
    descriptionKey: 'search.desc.documents',
    href: '/operations/documents',
    keywords: ['documente', 'nir', 'pv', 'borderou', 'anexa', 'pdf', 'documents'],
    group: 'nav',
  },
  {
    id: 'outgoing',
    labelKey: 'nav.outgoing',
    descriptionKey: 'search.desc.outgoing',
    href: '/operations/outgoing',
    keywords: ['expedieri', 'iesiri', 'transport', 'outgoing', 'trimitere', 'vanzare'],
    group: 'nav',
  },
  {
    id: 'outgoing-new',
    labelKey: 'search.label.newOutgoing',
    descriptionKey: 'search.desc.newOutgoing',
    href: '/operations/outgoing/new',
    keywords: ['expediere noua', 'iesire noua', 'new outgoing'],
    group: 'actions',
  },
  {
    id: 'treatment',
    labelKey: 'nav.treatment',
    descriptionKey: 'search.desc.treatment',
    href: '/operations/treatment',
    keywords: ['tratare', 'fractii', 'dezmembrare', 'treatment', 'fractions'],
    group: 'nav',
  },
  {
    id: 'stock',
    labelKey: 'nav.stock',
    descriptionKey: 'search.desc.stock',
    href: '/operations/stock',
    keywords: ['stoc', 'inventar', 'stock', 'inventory', 'cantitati'],
    group: 'nav',
  },
  {
    id: 'reports',
    labelKey: 'nav.reports',
    descriptionKey: 'search.desc.reports',
    href: '/reports',
    keywords: ['rapoarte', 'raport', 'anpm', 'ecotic', 'saga', 'contabilitate', 'reports', 'export'],
    group: 'nav',
  },
  // ── Catalog ─────────────────────────────────────────────────────────────────
  {
    id: 'wastes',
    labelKey: 'nav.wastes',
    descriptionKey: 'search.desc.wastes',
    href: '/catalog/wastes',
    keywords: ['deseuri', 'materiale', 'catalog', 'wastes', 'materials', 'hg856'],
    group: 'nav',
  },
  {
    id: 'companies',
    labelKey: 'nav.companies',
    descriptionKey: 'search.desc.companies',
    href: '/catalog/companies',
    keywords: ['companii', 'firme', 'furnizori', 'colectori', 'transportatori', 'companies', 'firms'],
    group: 'nav',
  },
  {
    id: 'persons',
    labelKey: 'nav.persons',
    descriptionKey: 'search.desc.persons',
    href: '/catalog/persons',
    keywords: ['persoane', 'persoane fizice', 'pf', 'persons', 'individuals', 'cnp'],
    group: 'nav',
  },
  // ── Settings ─────────────────────────────────────────────────────────────────
  {
    id: 'settings-security',
    labelKey: 'nav.security',
    descriptionKey: 'search.desc.security',
    href: '/settings/security',
    keywords: ['securitate', 'parola', 'mfa', 'autentificare', 'security', 'password', '2fa', 'totp'],
    group: 'settings',
  },
  {
    id: 'settings-tenant-profile',
    labelKey: 'nav.tenantProfile',
    descriptionKey: 'search.desc.tenantProfile',
    href: '/settings/tenant-profile',
    keywords: ['profil', 'firma', 'autorizatie', 'flota', 'vehicule', 'soferi', 'profile', 'company info', 'fleet'],
    group: 'settings',
  },
  {
    id: 'settings-users',
    labelKey: 'settings.nav.users',
    descriptionKey: 'search.desc.users',
    href: '/settings/users',
    keywords: ['utilizatori', 'echipa', 'operatori', 'users', 'team', 'members'],
    group: 'settings',
  },
  {
    id: 'settings-flow-types',
    labelKey: 'nav.flowTypes',
    descriptionKey: 'search.desc.flowTypes',
    href: '/settings/flow-types',
    keywords: ['fluxuri', 'tipuri flux', 'deee', 'dba', 'metale', 'categorii', 'flow types', 'profiles'],
    group: 'settings',
  },
  {
    id: 'settings-tariff-types',
    labelKey: 'nav.tariffTypes',
    descriptionKey: 'search.desc.tariffTypes',
    href: '/settings/tariff-types',
    keywords: ['tarife', 'preturi', 'tariff', 'prices', 'pret kg'],
    group: 'settings',
  },
  {
    id: 'settings-document-sequences',
    labelKey: 'nav.documentSequences',
    descriptionKey: 'search.desc.documentSequences',
    href: '/settings/document-sequences',
    keywords: ['numerotare', 'serii documente', 'prefix', 'sequence', 'numbering', 'nir series'],
    group: 'settings',
  },
  {
    id: 'settings-app-config',
    labelKey: 'settings.features.title',
    descriptionKey: 'search.desc.appConfig',
    href: '/settings/app-config',
    keywords: ['configurare', 'setari aplicatie', 'colectare config', 'app config', 'settings', 'functionalitati'],
    group: 'settings',
  },
  {
    id: 'settings-company-documents',
    labelKey: 'settings.tabs.companyDocuments',
    descriptionKey: 'search.desc.companyDocuments',
    href: '/settings/company-documents',
    keywords: ['documente companie', 'antet', 'header', 'company documents'],
    group: 'settings',
  },
  {
    id: 'settings-logs',
    labelKey: 'settings.logs.title',
    descriptionKey: 'search.desc.logs',
    href: '/settings/logs',
    keywords: ['jurnale', 'audit', 'activitate', 'loguri', 'logs', 'activity', 'events'],
    group: 'settings',
  },
  // ── Help & support ───────────────────────────────────────────────────────────
  {
    id: 'help',
    labelKey: 'nav.help',
    descriptionKey: 'search.desc.help',
    href: '/help',
    keywords: ['ajutor', 'ghid', 'tutorial', 'help', 'guide', 'documentation'],
    group: 'nav',
  },
  {
    id: 'support',
    labelKey: 'nav.support',
    descriptionKey: 'search.desc.support',
    href: '/support',
    keywords: ['suport', 'ticket', 'problema', 'support', 'issue', 'contact'],
    group: 'nav',
  },
  {
    id: 'updates',
    labelKey: 'nav.updates',
    descriptionKey: 'search.desc.updates',
    href: '/updates',
    keywords: ['noutati', 'actualizari', 'changelog', 'updates', 'release notes'],
    group: 'nav',
  },
  // Every menu destination and deep setting is reachable from Ctrl K (DESIGN.md §10).
  {
    id: 'pickup-schedule',
    labelKey: 'nav.pickup_schedule',
    href: '/operations/pickup-schedule',
    keywords: ['program', 'ridicari', 'ridicare', 'comenzi', 'traseu', 'pickup'],
    group: 'nav',
  },
  {
    id: 'containers',
    labelKey: 'nav.containers',
    href: '/containers',
    keywords: ['containere', 'container', 'bena', 'presa'],
    group: 'nav',
  },
  {
    id: 'financials',
    labelKey: 'nav.financials',
    href: '/financials',
    keywords: ['financiar', 'cheltuieli', 'venituri', 'profit', 'bani'],
    group: 'nav',
  },
  {
    id: 'billing',
    labelKey: 'nav.contabilitate',
    href: '/contabilitate',
    keywords: ['facturare', 'facturi', 'factura', 'efactura', 'e-factura', 'contabilitate'],
    group: 'nav',
  },
  {
    id: 'nexus',
    labelKey: 'nav.nexus',
    href: '/nexus',
    keywords: ['nexus', 'saga', 'sincronizare', 'export contabil'],
    group: 'nav',
  },
  {
    id: 'crm',
    labelKey: 'nav.crm',
    href: '/crm',
    keywords: ['crm', 'oferte', 'oferta', 'prospecti', 'clienti'],
    group: 'nav',
  },
  {
    id: 'hr',
    labelKey: 'nav.hr',
    href: '/hr',
    keywords: ['hr', 'angajati', 'pontaj', 'concedii', 'personal'],
    group: 'nav',
  },
  {
    id: 'market-prices',
    labelKey: 'nav.marketPrices',
    href: '/catalog/market-prices',
    keywords: ['preturi', 'piata', 'lme', 'cotatii'],
    group: 'nav',
  },
  {
    id: 'treatment-new',
    labelKey: 'search.label.newTreatment',
    href: '/operations/treatment/new',
    keywords: ['tratare noua', 'dezmembrare', 'fractii'],
    group: 'actions',
  },
  {
    id: 'settings-mobile',
    labelKey: 'settings.tabs.mobileApp',
    href: '/settings/mobile-app',
    keywords: ['aplicatie mobila', 'teren', 'apk', 'telefon'],
    group: 'settings',
  },
  {
    id: 'settings-notifications',
    labelKey: 'settings.tabs.emailNotifications',
    href: '/settings/notifications',
    keywords: ['notificari', 'email', 'alerte'],
    group: 'settings',
  },
  {
    id: 'settings-signature-pad',
    labelKey: 'settings.tabs.signaturePad',
    href: '/settings/signature-pad',
    keywords: ['semnatura', 'tableta', 'pad'],
    group: 'settings',
  },
  {
    id: 'settings-scales',
    labelKey: 'settings.tabs.scales',
    href: '/settings/scales',
    keywords: ['cantar', 'cantare', 'scale'],
    group: 'settings',
  },
  {
    id: 'settings-gdpr',
    labelKey: 'settings.tabs.gdpr',
    href: '/settings/gdpr',
    keywords: ['gdpr', 'date personale', 'stergere', 'registru'],
    group: 'settings',
  },
  {
    id: 'settings-api-keys',
    labelKey: 'settings.tabs.apiKeys',
    href: '/settings/api-keys',
    keywords: ['api', 'chei', 'integrare', 'webhook'],
    group: 'settings',
  },
];

// ── Fuzzy match ──────────────────────────────────────────────────────────────

/**
 * Score an item against a query string.
 * Returns 0 if no match, >0 if matched (higher = better).
 *
 * Strategy:
 *  - Each query word must appear somewhere in the combined text.
 *  - If all words matched in the label → bonus score.
 *  - Prefix match on any field → highest bonus.
 */
export function scoreItem(item: SearchItem, query: string, labelText: string, descText: string): number {
  if (!query) return 1;
  const q = query.toLowerCase().trim();
  if (!q) return 1;

  const tokens = q.split(/\s+/).filter(Boolean);
  const haystack = [
    labelText.toLowerCase(),
    descText.toLowerCase(),
    ...item.keywords,
  ].join(' ');

  const allMatch = tokens.every((tok) => haystack.includes(tok));
  if (!allMatch) return 0;

  let score = 1;
  // Bonus: all tokens found in label
  if (tokens.every((tok) => labelText.toLowerCase().includes(tok))) score += 2;
  // Bonus: label starts with query
  if (labelText.toLowerCase().startsWith(q)) score += 3;
  // Bonus: exact label match
  if (labelText.toLowerCase() === q) score += 5;

  return score;
}
